/**
 * localStorage for AI-disclaimer consent, holding two independent
 * records:
 *
 * - The **fallback** (`getStoredConsent` / `setStoredConsent`), used when
 *   the host does not inject both ECPP consent callbacks
 *   (`getConsentStatus` / `acceptConsent` on `SidekickApiContext`), such
 *   as when backend consent integration is disabled. It is the source of
 *   truth on that path.
 * - The **API cache** (`getCachedApiConsent` / `setCachedApiConsent`),
 *   which memoizes an ECPP-confirmed acceptance so a returning user
 *   skips the consent round-trip on entry. It is a cache, never a source
 *   of truth.
 *
 * The two use separate key namespaces on purpose. Sharing one key would
 * let an acceptance recorded while the consent API was disabled read
 * back later as ECPP-confirmed, and that user's consent would never
 * reach the backend.
 *
 * Kept deliberately tiny and framework-free so API-backed and local
 * consent remain interchangeable. Every read/write is wrapped in
 * try/catch (mirroring `@brightdrop/auth-client`'s storage helper) so a
 * disabled or quota-exhausted `localStorage` degrades to "not consented"
 * rather than throwing into the render path.
 */

import type { SidekickConsentTerms } from '../contexts/SidekickApiContext';

const CONSENT_STORAGE_PREFIX = 'sidekick.consent.v1';

const API_CONSENT_STORAGE_PREFIX = 'sidekick.consent.api.v1';

/**
 * How long a cached ECPP acceptance is trusted without confirmation.
 *
 * A sliding window, not an expiry: every confirmed read re-stamps
 * `acceptedAt`, while a degraded or failed one deliberately does not. It
 * therefore bounds only how long an acceptance the client could not
 * re-verify stays trusted — revocations and new terms revisions are
 * caught by `revalidateConsent` on entry, not by this.
 *
 * Deliberately long. Expiry forces a blocking live read, so a short
 * window would turn a CLPT outage into a lockout for users whose
 * acceptance is already on file.
 */
export const API_CONSENT_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Builds the per-user storage key. Consent is user-scoped, so a shared
 * browser must never leak one user's acceptance to another. Real user
 * ids live under a `.user.` segment and the no-userId fallback under a
 * separate `.anon` key, so a real id (even one literally `"anon"`) can
 * never collide with the anonymous bucket.
 */
const buildConsentKey = (userId?: string): string =>
  userId && userId.length > 0
    ? `${CONSENT_STORAGE_PREFIX}.user.${userId}`
    : `${CONSENT_STORAGE_PREFIX}.anon`;

/**
 * Reads whether the given user has locally accepted the AI disclaimer.
 * Returns `false` on any storage error or when no record exists.
 */
export const getStoredConsent = (userId?: string): boolean => {
  try {
    return localStorage.getItem(buildConsentKey(userId)) === 'true';
  } catch (error) {
    console.error('Failed to read Sidekick consent from localStorage:', error);
    return false;
  }
};

/**
 * Persists the given user's acceptance of the AI disclaimer. Storage
 * errors are swallowed (logged) so a failed write never blocks the
 * user from proceeding after they click Agree.
 */
export const setStoredConsent = (userId?: string): void => {
  try {
    localStorage.setItem(buildConsentKey(userId), 'true');
  } catch (error) {
    console.error('Failed to persist Sidekick consent to localStorage:', error);
  }
};

/**
 * A cached ECPP-confirmed acceptance. `acceptedAt` is an epoch
 * millisecond stamp measured against {@link API_CONSENT_CACHE_TTL_MS};
 * `code` and `revision` record which terms the user accepted, so a live
 * read reporting a different revision replaces the record.
 *
 * Both terms fields are required. A record that cannot name what was
 * accepted can never be confirmed against a later read, so it would
 * serve a hit that proves nothing; such a record is refused on write and
 * pruned on read rather than stored in a shape the reader must distrust.
 */
export type CachedApiConsent = {
  code: string;
  revision: string;
  acceptedAt: number;
};

/**
 * Builds the API-cache key. Unlike the fallback there is no anonymous
 * bucket: the consent API is keyed on a profile id, so a caller without
 * a user id has nothing cacheable and is refused outright.
 */
const buildApiConsentKey = (userId: string): string =>
  `${API_CONSENT_STORAGE_PREFIX}.user.${userId}`;

/**
 * Parses a stored record, returning `null` for anything unusable. A
 * cache entry written by an older format (or corrupted in place) is not
 * an error worth reporting — it is pruned and re-earned on the next live
 * read — so JSON failures are absorbed here rather than escaping to the
 * storage-level handler.
 */
const parseCachedApiConsent = (raw: string): CachedApiConsent | null => {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.acceptedAt !== 'number') {
    return null;
  }

  // Without both terms fields the record cannot be checked against the
  // terms a later read reports, so it would grant on nothing more than
  // its own existence.
  if (
    typeof record.code !== 'string' ||
    record.code.length === 0 ||
    typeof record.revision !== 'string' ||
    record.revision.length === 0
  ) {
    return null;
  }

  return {
    code: record.code,
    revision: record.revision,
    acceptedAt: record.acceptedAt,
  };
};

/**
 * Reads the cached ECPP acceptance for the given user, or `null` when
 * there is nothing usable — no user id, no record, a malformed record, a
 * record past its TTL, or unavailable storage. An expired or malformed
 * entry is pruned on the way out so it cannot be re-parsed on the next
 * read.
 *
 * @param userId - Profile id the acceptance is keyed on.
 * @returns The cached acceptance, or `null` to force a live read.
 */
export const getCachedApiConsent = (
  userId?: string
): CachedApiConsent | null => {
  if (userId === undefined || userId.length === 0) {
    return null;
  }

  const key = buildApiConsentKey(userId);

  try {
    const raw = localStorage.getItem(key);
    if (raw === null) {
      return null;
    }

    const parsed = parseCachedApiConsent(raw);
    if (parsed === null) {
      localStorage.removeItem(key);
      return null;
    }

    if (Date.now() - parsed.acceptedAt >= API_CONSENT_CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error(
      'Failed to read cached Sidekick consent from localStorage:',
      error
    );
    return null;
  }
};

/**
 * Caches an ECPP-confirmed acceptance for the given user. A missing user
 * id is a no-op, since the cache key would not identify anyone, as is a
 * terms record that names no revision, which the reader would discard
 * anyway.
 *
 * Callers are responsible for withholding degraded or non-durable
 * results; beyond the revision it requires, this helper stores whatever
 * it is handed.
 *
 * @param userId - Profile id the acceptance is keyed on.
 * @param terms - Terms in force when the acceptance was confirmed.
 */
export const setCachedApiConsent = (
  userId: string | undefined,
  terms?: SidekickConsentTerms
): void => {
  if (userId === undefined || userId.length === 0) {
    return;
  }

  const { code, revision } = terms ?? {};
  if (
    code === undefined ||
    code.length === 0 ||
    revision === undefined ||
    revision.length === 0
  ) {
    return;
  }

  const record: CachedApiConsent = {
    code,
    revision,
    acceptedAt: Date.now(),
  };

  try {
    localStorage.setItem(buildApiConsentKey(userId), JSON.stringify(record));
  } catch (error) {
    console.error('Failed to cache Sidekick consent to localStorage:', error);
  }
};

/**
 * Drops the cached acceptance for the given user, forcing the next entry
 * to read consent live.
 *
 * @param userId - Profile id the acceptance is keyed on.
 */
export const clearCachedApiConsent = (userId?: string): void => {
  if (userId === undefined || userId.length === 0) {
    return;
  }

  try {
    localStorage.removeItem(buildApiConsentKey(userId));
  } catch (error) {
    console.error(
      'Failed to clear cached Sidekick consent from localStorage:',
      error
    );
  }
};
